import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import propTypes from 'prop-types';
import useAssemblyActions from '../../hooks/useAssemblyActions';
import OptionsResult from './OptionsResult';
import BtnComments from '../BtnComments';
import useReports from '../../hooks/useReports';
import useAssemblyReadable from '../../hooks/useAssemblyReadable';

const SubjectInfo = ({ subject, assembly }) => {
  const votes = useMemo(
    () => (subject.options && subject.options.reduce((acc, curr) => acc + (curr.votes || []).length, 0)) || 0,
    [subject],
  );
  return (
    <>
      <hr />
      <div className="d-flex w-100 flex-column">
        <b className="mb-1">{subject.title}</b>
        {['opened', 'closed'].includes(assembly.status) && (
          <>
            <small className="d-flex align-items-center justify-content-between">
              <span>
                {subject.options && subject.options.length > 0 && (
                  <>
                    {' '}
                    {votes}
                    &nbsp;voto
                    {votes !== 1 ? 's' : ''}
                  </>
                )}
              </span>
              <BtnComments
                comments={subject.comments.length}
                assemblyId={assembly._id}
                condominiumId={assembly.condominium}
                subjectId={subject._id}
              />
            </small>
            <div className="d-flex w-100 ">
              {subject.options &&
                subject.options.map((option, key) => (
                  <OptionsResult
                    option={option}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`option-${key}`}
                    total={votes}
                    badge={key % 2 === 0 ? 'badge-purple' : 'badge-info'}
                  />
                ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default function AssemblyCard({ assembly, onCompleted, isJanitor }) {
  const { commentsReport, votesReport } = useReports();
  const { open, close, opening, closing } = useAssemblyActions({
    assembly,
    onCompleted,
  });
  const readableAssembly = useAssemblyReadable(assembly);
  const isSubmitting = useMemo(() => opening || closing, [opening, closing]);

  return (
    <div className="list-group-item list-group-item-action flex-column align-items-start">
      <div className="d-flex w-100 flex-row justify-content-between">
        <b className="mb-2">{readableAssembly.title}</b>
        {(isJanitor && assembly.status === 'draft' && (
          <small>
            <Link to={`/condominiums/${assembly.condominium}/${readableAssembly.path}/${assembly._id}/edit`}>
              editar
            </Link>
          </small>
        )) || (
          <span
            className={`badge badge-pill d-flex align-items-center justify-content-center text-white ${
              assembly.status === 'opened' ? ' badge-success' : ' badge-danger'
            }`}
          >
            {assembly.status === 'opened' ? 'Aberta' : 'Fechada'}
          </span>
        )}
      </div>
      {assembly.subjects.map(subject => (
        <SubjectInfo subject={subject} assembly={assembly} key={subject._id} />
      ))}
      {['closed'].includes(assembly.status) && isJanitor && (
        <>
          <button className="btn btn-block btn-primary mt-3" type="button" onClick={() => votesReport(assembly._id)}>
            Salvar relatório de votação
          </button>
          <button
            className="btn btn-block btn-outline-primary mt-2"
            type="button"
            onClick={() => commentsReport(assembly._id)}
          >
            Salvar relatório de comentários
          </button>
        </>
      )}
      {['opened'].includes(assembly.status) && (
        <Link
          className="btn btn-sm btn-block btn-primary mt-3"
          to={`/condominiums/${assembly.condominium}/${readableAssembly.path}/${assembly._id}`}
        >
          Ver detalhe
          {!isJanitor && ` e votar`}
        </Link>
      )}
      {isJanitor && assembly.status === 'draft' && (
        <button className="btn btn-sm btn-block btn-outline-primary mt-3" type="button" onClick={() => open()}>
          {`${isSubmitting ? 'Abrindo' : 'Abrir'} ${readableAssembly.title.toLowerCase()} e convidar condôminos`}
        </button>
      )}
      {isJanitor && assembly.status === 'opened' && (
        <button className="btn btn-sm btn-block btn-outline-primary mt-3" type="button" onClick={() => close()}>
          {`${isSubmitting ? 'Encerrando' : 'Encerrar'} ${readableAssembly.title.toLowerCase()}`}
        </button>
      )}
    </div>
  );
}

AssemblyCard.propTypes = {
  assembly: propTypes.object.isRequired,
  onCompleted: propTypes.func.isRequired,
  isJanitor: propTypes.bool.isRequired,
};

SubjectInfo.propTypes = {
  subject: propTypes.object.isRequired,
  assembly: propTypes.object.isRequired,
};
